// src/pages/OrderTrackingPublicPage.jsx - SENSOR DE RASTREIO PÚBLICO (TIM PJ EDITION)
import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { OrderExecutionEngine } from '../api/OrderExecutionEngine';
import { FiSearch, FiPackage, FiShield, FiCheckCircle, FiClock, FiAlertCircle, FiInfo } from 'react-icons/fi';
import { toast } from 'react-hot-toast';

export default function OrderTrackingPublicPage() {
    const [searchParams] = useSearchParams();
    const [protocolo, setProtocolo] = useState(searchParams.get('protocolo') || '');
    const [token, setToken] = useState(searchParams.get('token') || '');
    const [orderData, setOrderData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [showSharePopup, setShowSharePopup] = useState(false);
    const [shareOptions, setShareOptions] = useState({ vendas: true, rastreio: true, indicar: true });

    const handleShare = async () => {
        let text = "🚀 Olá! Veja as novidades da TIM PJ:\n\n";
        if (shareOptions.vendas) text += "🛒 *Sistema de Vendas:* https://loja.timpj.com.br\n";
        if (shareOptions.rastreio) text += `📦 *Acompanhamento de Pedidos:* https://loja.timpj.com.br/statuspedido\n`;
        if (shareOptions.indicar) text += "🤝 *Indicação Especial:* https://loja.timpj.com.br\n";
        
        try {
            if (navigator.share) {
                await navigator.share({
                    title: 'Portal TIM PJ',
                    text: text,
                });
                toast.success("Compartilhado com sucesso!");
            } else {
                navigator.clipboard.writeText(text);
                toast.success("Copiado para a área de transferência!");
            }
        } catch (error) {
            console.log('Error sharing', error);
        }
        setShowSharePopup(false);
    };

    const getTimelineSteps = (status) => {
        const defaultSteps = [
            { label: 'Pedido Recebido', done: false, active: false },
            { label: 'Em Análise', done: false, active: false },
            { label: 'Processamento TIM', done: false, active: false },
            { label: 'Concluído', done: false, active: false }
        ];

        let currentIndex = 0;
        if (status === 'PENDENTE' || status === 'NOVO') currentIndex = 0;
        else if (status?.includes('ANALISE') || status === 'AGUARDANDO_DOCS' || status === 'Aguardando Documentos' || status === 'Erro na Documentação') currentIndex = 1;
        else if (status?.includes('TIM') || status === 'ATRIBUIDO' || status === 'Emissão de Contrato') currentIndex = 2;
        else if (status === 'CONCLUIDO' || status === 'FINALIZADO') currentIndex = 3;

        return defaultSteps.map((step, idx) => ({
            ...step,
            done: idx < currentIndex,
            active: idx === currentIndex
        }));
    };

    // ⚡ Efeito de Captura Automática via Link
    useEffect(() => {
        if (protocolo && token) {
            handleSearch(null, protocolo, token);
        }
    }, []);

    const handleSearch = async (e, p = protocolo, t = token) => {
        if (e) e.preventDefault();
        if (!p || !t) return toast.error("INSIRA PROTOCOLO E TOKEN");

        setLoading(true);
        try {
            const res = await OrderExecutionEngine.verificarRastreio(t, p);
            if (res?.success) {
                setOrderData(res.order);
                toast.success("Rastreio Localizado!");
            } else {
                toast.error("Credenciais inválidas ou não encontradas.");
                setOrderData(null);
            }
        } catch (err) {
            toast.error("Erro na consulta do sistema.");
        } finally {
            setLoading(false);
        }
    };

    const isClientActionRequired = ['Aguardando Documentos', 'Erro na Documentação', 'Assinatura de Contrato', 'Restrição TIM'].includes(orderData?.status);
    const lastLog = orderData?.historico?.length > 0 ? orderData.historico[orderData.historico.length - 1] : null;

    return (
        <div className="min-h-screen bg-[#000614] text-white p-6 md:p-12 flex flex-col items-center justify-center font-sans selection:bg-blue-500/30">
            {/* Background Glows */}
            <div className="fixed top-0 left-1/2 -translate-x-1/2 w-[800px] h-[500px] bg-blue-600/10 rounded-full blur-[120px] pointer-events-none"></div>

            <div className="w-full max-w-4xl space-y-12 relative z-10">
                
                {/* HEADER DA TIM PJ */}
                <div className="flex flex-col items-center text-center space-y-4 mb-16">
                    <div className="w-16 h-16 bg-blue-600 rounded-2xl flex items-center justify-center shadow-[0_0_30px_rgba(37,99,235,0.4)] mb-4">
                        <FiPackage size={32} className="text-white" />
                    </div>
                    <h1 className="text-4xl md:text-5xl font-black uppercase tracking-tight text-white">
                        Rastreio de <span className="text-blue-500">Pedidos</span>
                    </h1>
                    <p className="text-xs uppercase font-bold tracking-[0.4em] text-blue-300/50">
                        Portal Oficial TIM PJ
                    </p>
                </div>

                {/* FORMULÁRIO DE BUSCA */}
                <form onSubmit={handleSearch} className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-4 bg-[#00102a]/80 backdrop-blur-xl p-6 rounded-[2rem] border border-blue-500/20 shadow-2xl">
                    <div className="relative">
                        <input 
                            placeholder="PROTOCOLO DO PEDIDO" 
                            value={protocolo}
                            onChange={(e) => setProtocolo(e.target.value)}
                            className="w-full bg-[#000a1f] border border-blue-500/30 rounded-xl p-5 text-sm font-bold uppercase tracking-widest outline-none focus:border-blue-400 focus:shadow-[0_0_15px_rgba(59,130,246,0.2)] transition-all placeholder:text-blue-200/20 text-blue-50"
                        />
                    </div>
                    <div className="relative">
                        <input 
                            placeholder="TOKEN DE SEGURANÇA" 
                            value={token}
                            type="password"
                            onChange={(e) => setToken(e.target.value)}
                            className="w-full bg-[#000a1f] border border-blue-500/30 rounded-xl p-5 text-sm font-bold uppercase tracking-widest outline-none focus:border-blue-400 focus:shadow-[0_0_15px_rgba(59,130,246,0.2)] transition-all placeholder:text-blue-200/20 text-blue-50"
                        />
                    </div>
                    <button type="submit" className="bg-blue-600 text-white font-black uppercase text-xs tracking-widest rounded-xl px-10 py-5 hover:bg-blue-500 active:scale-95 transition-all shadow-[0_0_20px_rgba(37,99,235,0.4)] hover:shadow-[0_0_30px_rgba(37,99,235,0.6)] flex items-center justify-center gap-3">
                        {loading ? <FiClock className="animate-spin" size={18} /> : <FiSearch size={18} />} BUSCAR
                    </button>
                </form>

                {/* RESULTADO OFICIAL */}
                {orderData && (
                    <div className="bg-[#00102a]/90 backdrop-blur-2xl border border-blue-500/20 rounded-[3rem] p-8 md:p-12 animate-in fade-in slide-in-from-bottom-10 duration-700 shadow-2xl">
                        
                        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-12">
                            <div>
                                <span className="text-[10px] font-black uppercase text-blue-400/60 tracking-[0.3em] block mb-3">Status Oficial da Operação</span>
                                <h2 className="text-3xl md:text-4xl font-black uppercase text-white tracking-tight">{orderData.status}</h2>
                            </div>
                            <div className="bg-blue-600/10 border border-blue-500/30 px-6 py-3 rounded-2xl flex items-center gap-3">
                                <span className="text-[10px] font-black uppercase text-blue-400/60 tracking-widest">Protocolo:</span>
                                <span className="text-sm font-mono font-bold text-blue-300">{orderData.protocolo}</span>
                            </div>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 py-8 border-y border-blue-500/10">
                            <div>
                                <span className="text-[10px] font-black uppercase text-blue-400/60 tracking-widest block mb-2">Cliente / Empresa</span>
                                <span className="text-lg font-bold text-white uppercase">{orderData.empresa?.razaoSocial || orderData.clienteNome || '-'}</span>
                            </div>
                            <div>
                                <span className="text-[10px] font-black uppercase text-blue-400/60 tracking-widest block mb-2">Última Atualização</span>
                                <span className="text-lg font-bold text-white uppercase">{new Date(orderData.updatedAt || orderData.createdAt).toLocaleString('pt-BR')}</span>
                            </div>
                        </div>

                        {/* MENSAGEM DA CENTRAL (SE HOUVER AÇÃO NECESSÁRIA) */}
                        {lastLog && isClientActionRequired && (
                            <div className="mt-8 mb-4 p-6 bg-orange-500/10 border border-orange-500/30 rounded-3xl flex items-start gap-4">
                                <div className="p-3 bg-orange-500/20 rounded-xl text-orange-400 shrink-0">
                                    <FiAlertCircle size={24} />
                                </div>
                                <div>
                                    <h4 className="text-xs font-black uppercase text-orange-400 tracking-widest mb-2">Ação Necessária / Mensagem da Central</h4>
                                    <p className="text-sm text-orange-100 font-medium leading-relaxed">{lastLog.comentario || 'Verifique com seu consultor.'}</p>
                                </div>
                            </div>
                        )}

                        {/* A MENSAGEM DA CENTRAL (INFORMATIVA) FOI REMOVIDA PARA EVITAR VAZAMENTO DE LOGS INTERNOS */}

                        {/* TIMELINE HORIZONTAL / VERTICAL */}
                        <div className="mt-12 bg-[#000a1f] p-8 rounded-3xl border border-blue-500/10">
                            <span className="text-[10px] font-black uppercase text-blue-400/60 tracking-widest block mb-10 text-center">Progresso da Implantação</span>
                            <div className="flex flex-col md:flex-row justify-between relative gap-8 md:gap-0">
                                {/* Linha conectora de fundo */}
                                <div className="absolute top-6 left-[10%] right-[10%] h-1 bg-blue-900/30 hidden md:block rounded-full z-0"></div>
                                
                                {getTimelineSteps(orderData.status).map((step, idx) => (
                                    <div key={idx} className="relative z-10 flex flex-row md:flex-col items-center gap-6 md:gap-4 md:w-1/4">
                                        <div className={`w-12 h-12 rounded-2xl flex items-center justify-center border transition-all duration-700 shrink-0
                                            ${step.done ? 'bg-blue-600 border-blue-500 text-white shadow-[0_0_20px_rgba(37,99,235,0.6)]' 
                                            : step.active ? 'bg-[#00102a] border-blue-400 text-blue-400 animate-pulse shadow-[0_0_15px_rgba(96,165,250,0.3)]' 
                                            : 'bg-[#000a1f] border-blue-900/50 text-blue-900/50'}`}>
                                            {step.done ? <FiCheckCircle size={20} /> : <div className="w-3 h-3 rounded-full bg-current" />}
                                        </div>
                                        <span className={`text-[10px] font-black uppercase tracking-wider text-center
                                            ${step.done ? 'text-white' : step.active ? 'text-blue-300' : 'text-blue-800'}`}>
                                            {step.label}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* FOOTER DO CARD */}
                        <div className="mt-12 pt-6 border-t border-blue-500/10 flex flex-col md:flex-row items-center justify-between gap-4">
                            <div className="flex flex-col md:flex-row gap-4 w-full md:w-auto">
                                <button onClick={() => window.location.href = 'https://loja.timpj.com.br'} className="bg-blue-600/20 text-blue-400 border border-blue-500/30 px-6 py-3 rounded-xl font-black uppercase tracking-widest text-xs hover:bg-blue-600/40 transition-all flex-1 md:flex-none">
                                    Realizar Novo Pedido
                                </button>
                                <button onClick={() => setShowSharePopup(true)} className="bg-green-600/20 text-green-400 border border-green-500/30 px-6 py-3 rounded-xl font-black uppercase tracking-widest text-xs hover:bg-green-600/40 transition-all flex-1 md:flex-none">
                                    Compartilhar
                                </button>
                            </div>
                            <div className="flex items-center gap-3 text-[10px] font-black uppercase tracking-widest text-blue-400/40">
                                <FiShield size={16} /> 
                                <span>Ambiente Criptografado • TIM PJ Security</span>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* POPUP DE COMPARTILHAMENTO */}
            {showSharePopup && (
                <div className="fixed inset-0 z-[999999] flex items-center justify-center p-6 bg-black/80 backdrop-blur-md">
                    <div className="bg-[#00102a] border border-blue-500/30 p-8 rounded-[2rem] max-w-md w-full shadow-[0_0_50px_rgba(37,99,235,0.15)] animate-in zoom-in duration-300">
                        <h3 className="text-xl font-black uppercase text-white mb-6 tracking-widest text-center">Compartilhar com Amigo/Colega</h3>
                        
                        <div className="space-y-4 mb-8 bg-[#000a1f] p-6 rounded-2xl border border-blue-500/10">
                            <label className="flex items-center gap-4 cursor-pointer group">
                                <input type="checkbox" checked={shareOptions.vendas} onChange={e => setShareOptions({...shareOptions, vendas: e.target.checked})} className="w-5 h-5 accent-blue-500" />
                                <span className="text-sm font-bold text-blue-100 uppercase tracking-widest group-hover:text-white transition-colors">Sistema de Vendas</span>
                            </label>
                            <label className="flex items-center gap-4 cursor-pointer group">
                                <input type="checkbox" checked={shareOptions.rastreio} onChange={e => setShareOptions({...shareOptions, rastreio: e.target.checked})} className="w-5 h-5 accent-blue-500" />
                                <span className="text-sm font-bold text-blue-100 uppercase tracking-widest group-hover:text-white transition-colors">Status de Pedidos</span>
                            </label>
                            <label className="flex items-center gap-4 cursor-pointer group">
                                <input type="checkbox" checked={shareOptions.indicar} onChange={e => setShareOptions({...shareOptions, indicar: e.target.checked})} className="w-5 h-5 accent-blue-500" />
                                <span className="text-sm font-bold text-blue-100 uppercase tracking-widest group-hover:text-white transition-colors">Indicar</span>
                            </label>
                        </div>

                        <div className="flex gap-4">
                            <button onClick={() => setShowSharePopup(false)} className="flex-1 py-4 rounded-xl border border-blue-500/30 text-blue-400 font-black uppercase tracking-widest text-xs hover:bg-blue-500/10 transition-colors">
                                Cancelar
                            </button>
                            <button onClick={handleShare} className="flex-1 py-4 rounded-xl bg-blue-600 text-white font-black uppercase tracking-widest text-xs shadow-[0_0_20px_rgba(37,99,235,0.4)] hover:bg-blue-500 transition-all hover:scale-105 active:scale-95">
                                Compartilhar
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}